import { useEffect } from "react";
import { X } from "lucide-react";
import { COMPANY } from "../lib/constants";

export const LegalModal = ({ open, onClose, type }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid={`legal-modal-${type}`}
      className="fixed inset-0 z-[70] flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full md:max-w-3xl max-h-[92vh] md:max-h-[88vh] overflow-hidden rounded-t-3xl md:rounded-3xl bg-white border border-black/10 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.5)] slide-up"
      >
        <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-black/8 sticky top-0 bg-white z-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45">
              · {type === "impressum" ? "Rechtliches" : "Datenschutz"}
            </div>
            <div className="mt-1 text-xl md:text-2xl font-medium tracking-tight text-black">
              {type === "impressum" ? "Impressum" : "Datenschutzerklärung"}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid={`legal-close-${type}`}
            aria-label="Schließen"
            className="w-10 h-10 rounded-full hover:bg-black/5 flex items-center justify-center text-black/60 hover:text-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 md:px-8 py-6 md:py-8 overflow-y-auto max-h-[calc(92vh-90px)] md:max-h-[calc(88vh-90px)] text-sm md:text-[15px] leading-relaxed text-black/80 space-y-5">
          {type === "impressum" ? <ImpressumContent /> : <DatenschutzContent />}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/45 mb-1.5">
      {title}
    </div>
    <div className="text-black">{children}</div>
  </div>
);

const ImpressumContent = () => (
  <>
    <Section title="Angaben gemäß § 5 DDG">
      <div>{COMPANY.name}</div>
      {COMPANY.impressum.address.map((line, i) => (
        <div key={i}>{line}</div>
      ))}
    </Section>
    <Section title="Kontakt">
      <div>
        Telefon: <a className="underline underline-offset-4" href={`tel:${COMPANY.phoneRaw}`}>{COMPANY.phone}</a>
      </div>
      <div>
        E-Mail:{" "}
        <a className="underline underline-offset-4" href={`mailto:${COMPANY.email}`}>
          {COMPANY.email}
        </a>
      </div>
    </Section>
    <Section title="Vertreten durch">
      <div>{COMPANY.impressum.ceo}</div>
      <div className="text-black/60 text-sm mt-1">Geschäftsinhaber</div>
    </Section>
    <Section title="Umsatzsteuer-ID">
      <div>{COMPANY.impressum.ustId}</div>
    </Section>
    <Section title="EU-Streitschlichtung">
      <p className="text-black/70">
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a
          href="https://ec.europa.eu/consumers/odr"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4"
        >
          https://ec.europa.eu/consumers/odr
        </a>
        . Unsere E-Mail-Adresse finden Sie oben im Impressum.
      </p>
    </Section>
    <Section title="Haftung für Inhalte">
      <p className="text-black/70">
        Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten
        nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als
        Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde
        Informationen zu überwachen oder nach Umständen zu forschen, die auf eine
        rechtswidrige Tätigkeit hinweisen.
      </p>
      <p className="text-black/70 mt-3">
        Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den
        allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch
        erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei
        Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend
        entfernen.
      </p>
    </Section>
  </>
);

const DatenschutzContent = () => (
  <>
    <Section title="1. Verantwortlicher">
      <p className="text-black/70">
        Verantwortlich für die Datenverarbeitung auf dieser Website im Sinne der
        Datenschutz-Grundverordnung (DSGVO) und des Bundesdatenschutzgesetzes
        (BDSG) ist:
      </p>
      <div className="mt-3 rounded-2xl border border-black/10 p-4 bg-black/[0.02]">
        <div className="font-medium">{COMPANY.name}</div>
        {COMPANY.impressum.address.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        <div className="mt-2">
          Geschäftsinhaber: <strong>{COMPANY.impressum.ceo}</strong>
        </div>
        <div className="mt-2">
          Telefon:{" "}
          <a className="underline underline-offset-4" href={`tel:${COMPANY.phoneRaw}`}>
            {COMPANY.phone}
          </a>
        </div>
        <div>
          E-Mail:{" "}
          <a className="underline underline-offset-4" href={`mailto:${COMPANY.email}`}>
            {COMPANY.email}
          </a>
        </div>
      </div>
    </Section>

    <Section title="2. Allgemeines zur Datenverarbeitung">
      <p className="text-black/70">
        Der Schutz Ihrer personenbezogenen Daten ist uns ein wichtiges Anliegen.
        Wir verarbeiten personenbezogene Daten (z. B. Name, Anschrift,
        Telefonnummer, E-Mail) nur im Rahmen der geltenden gesetzlichen
        Bestimmungen, insbesondere der DSGVO und des BDSG. Personenbezogene
        Daten werden nur erhoben, verarbeitet oder genutzt, wenn Sie in die
        Erhebung, Verarbeitung oder Nutzung einwilligen oder eine gesetzliche
        Erlaubnis dafür besteht.
      </p>
    </Section>

    <Section title="3. Hosting & Bereitstellung der Website">
      <p className="text-black/70">
        Diese Website wird bei einem externen Dienstleister gehostet
        (Auftragsverarbeiter):
      </p>
      <div className="mt-3 rounded-2xl border border-black/10 p-4 bg-black/[0.02]">
        <div className="font-medium">IONOS SE</div>
        <div>Elgendorfer Straße 57</div>
        <div>56410 Montabaur</div>
        <div>Deutschland</div>
        <div className="mt-2">
          Datenschutz:{" "}
          <a
            href="https://www.ionos.de/terms-gtc/terms-privacy"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4"
          >
            www.ionos.de/terms-gtc/terms-privacy
          </a>
        </div>
      </div>
      <p className="text-black/70 mt-3">
        Der Hoster verarbeitet in unserem Auftrag Meta-/Kommunikationsdaten,
        Kontaktdaten, IP-Adressen und Zugriffsdaten (Server-Logfiles), die
        beim Besuch dieser Website automatisch entstehen. Mit dem Hoster
        besteht ein Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 DSGVO.
        Die Verarbeitung erfolgt zur Bereitstellung einer sicheren,
        funktionsfähigen Website (Art. 6 Abs. 1 lit. f DSGVO – berechtigtes
        Interesse).
      </p>
    </Section>

    <Section title="4. Domain & SSL/TLS-Verschlüsselung">
      <p className="text-black/70">
        Diese Website wird unter der Domain{" "}
        <strong>bremer-recycling-service.de</strong> betrieben. Zur Sicherheit
        Ihrer Datenübertragung nutzt diese Website eine SSL/TLS-Verschlüsselung.
        Eine verschlüsselte Verbindung erkennen Sie am „https://“ und dem
        Schloss-Symbol in Ihrer Browserzeile.
      </p>
    </Section>

    <Section title="5. Server-Logfiles">
      <p className="text-black/70">
        Beim Aufruf unserer Website werden automatisch Informationen in
        sogenannten Server-Logfiles gespeichert, die Ihr Browser übermittelt:
      </p>
      <ul className="mt-2 space-y-1 list-disc pl-5 text-black/70">
        <li>Browsertyp und Browserversion</li>
        <li>Verwendetes Betriebssystem</li>
        <li>Referrer-URL</li>
        <li>Hostname des zugreifenden Rechners</li>
        <li>Uhrzeit der Serveranfrage</li>
        <li>IP-Adresse (anonymisiert bzw. gekürzt)</li>
      </ul>
      <p className="text-black/70 mt-3">
        Diese Daten sind nicht bestimmten Personen zuordenbar. Eine
        Zusammenführung mit anderen Datenquellen findet nicht statt.
        Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
        an der technisch fehlerfreien Auslieferung und Sicherheit).
        Speicherdauer: max. 30 Tage.
      </p>
    </Section>

    <Section title="6. Kontaktaufnahme (Telefon, E-Mail, WhatsApp, Formular)">
      <p className="text-black/70">
        Wenn Sie uns per Telefon, E-Mail, WhatsApp oder über unser
        Angebotsformular kontaktieren, werden Ihre Angaben (Name,
        Telefonnummer, ggf. E-Mail-Adresse, Adresse und Beschreibung Ihrer
        Anfrage) ausschließlich zur Bearbeitung Ihres Anliegens verarbeitet.
      </p>
      <p className="text-black/70 mt-3">
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO
        (vorvertragliche/vertragliche Maßnahmen) sowie Art. 6 Abs. 1 lit. a
        DSGVO (Einwilligung), sofern Sie diese im Angebotsformular erteilt
        haben. Ihre Daten werden gelöscht, sobald sie für die Zweckerreichung
        nicht mehr erforderlich sind – spätestens nach Ablauf gesetzlicher
        Aufbewahrungsfristen (i. d. R. 6–10 Jahre nach HGB/AO).
      </p>
    </Section>

    <Section title="7. WhatsApp Business (Meta Platforms)">
      <p className="text-black/70">
        Wir bieten Ihnen die Möglichkeit, uns über den Messenger WhatsApp zu
        kontaktieren. Anbieter ist die WhatsApp Ireland Limited, 4 Grand Canal
        Square, Grand Canal Harbour, Dublin 2, Irland (Meta Platforms Ireland
        Ltd.). Bei der Kontaktaufnahme über WhatsApp werden Ihre Telefonnummer
        und Ihre Nachricht an WhatsApp übertragen. WhatsApp verarbeitet Daten
        teilweise außerhalb der EU (u. a. USA).
      </p>
      <p className="text-black/70 mt-3">
        Die Verarbeitung erfolgt auf Grundlage Ihrer Einwilligung durch aktive
        Kontaktaufnahme (Art. 6 Abs. 1 lit. a DSGVO) und unseres
        berechtigten Interesses an schneller Kommunikation (Art. 6 Abs. 1 lit.
        f DSGVO). Details entnehmen Sie der{" "}
        <a
          href="https://www.whatsapp.com/legal/privacy-policy-eea"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4"
        >
          Datenschutzerklärung von WhatsApp
        </a>
        .
      </p>
    </Section>

    <Section title="8. Google-Bewertungen (externe Links)">
      <p className="text-black/70">
        Unsere Website verlinkt auf unser Google-Unternehmensprofil zur
        Anzeige öffentlicher Bewertungen. Anbieter ist die Google Ireland
        Limited, Gordon House, Barrow Street, Dublin 4, Irland. Beim Klick auf
        den Link werden Sie auf google.de weitergeleitet. Ab diesem Moment
        gelten die{" "}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noreferrer"
          className="underline underline-offset-4"
        >
          Datenschutzhinweise von Google
        </a>
        . Es findet keine automatische Einbettung von Google-Diensten auf
        dieser Seite statt.
      </p>
    </Section>

    <Section title="9. Instagram (externer Link)">
      <p className="text-black/70">
        Unsere Website verlinkt auf unser Instagram-Profil. Anbieter ist Meta
        Platforms Ireland Ltd., 4 Grand Canal Square, Dublin 2, Irland. Erst
        beim Klick auf den Link erfolgt eine Weiterleitung zu instagram.com.
        Es findet keine Einbindung von Instagram-Inhalten oder ein
        automatischer Datenaustausch auf unserer Seite statt.
      </p>
    </Section>

    <Section title="10. Cookies">
      <p className="text-black/70">
        Diese Website verwendet ausschließlich <strong>technisch notwendige</strong>{" "}
        Cookies (z. B. zur Speicherung des Cookie-Banner-Status). Es werden
        keine Marketing-, Analyse- oder Tracking-Cookies gesetzt. Es findet
        keine Auswertung Ihres Surfverhaltens statt. Rechtsgrundlage: § 25 Abs.
        2 TDDDG i. V. m. Art. 6 Abs. 1 lit. f DSGVO. Cookies können Sie in
        Ihrem Browser jederzeit löschen oder blockieren.
      </p>
    </Section>

    <Section title="11. Empfänger / Auftragsverarbeiter">
      <p className="text-black/70">
        Eine Weitergabe Ihrer Daten an Dritte erfolgt nicht, außer an unsere
        technischen Dienstleister im Rahmen einer Auftragsverarbeitung nach
        Art. 28 DSGVO (aktuell: IONOS SE für Hosting) sowie an Meta/WhatsApp
        bei aktiver Kontaktaufnahme über den Messenger. Eine Übermittlung in
        Drittländer außerhalb der EU/EWR erfolgt nur, wenn ein
        angemessenes Datenschutzniveau nach Art. 44 ff. DSGVO gesichert ist
        (z. B. EU-Standardvertragsklauseln bei Meta/WhatsApp).
      </p>
    </Section>

    <Section title="12. Speicherdauer">
      <p className="text-black/70">
        Wir speichern Ihre personenbezogenen Daten nur so lange, wie es zur
        Erfüllung des jeweiligen Zwecks erforderlich ist oder gesetzliche
        Aufbewahrungspflichten bestehen (i. d. R. 6 Jahre nach § 257 HGB, 10
        Jahre nach § 147 AO für steuerlich relevante Unterlagen). Nach Wegfall
        des Zwecks bzw. Ablauf der Fristen werden die Daten gelöscht.
      </p>
    </Section>

    <Section title="13. Ihre Rechte">
      <p className="text-black/70">
        Ihnen stehen als Betroffene folgende Rechte zu:
      </p>
      <ul className="mt-2 space-y-1 list-disc pl-5 text-black/70">
        <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
        <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
        <li>Recht auf Löschung (Art. 17 DSGVO)</li>
        <li>Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
        <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
        <li>Recht auf Widerspruch (Art. 21 DSGVO)</li>
        <li>Recht auf Widerruf erteilter Einwilligungen (Art. 7 Abs. 3 DSGVO)</li>
      </ul>
      <p className="text-black/70 mt-3">
        Zur Ausübung Ihrer Rechte genügt eine formlose Mitteilung an{" "}
        <a className="underline underline-offset-4" href={`mailto:${COMPANY.email}`}>
          {COMPANY.email}
        </a>
        .
      </p>
    </Section>

    <Section title="14. Beschwerderecht bei der Aufsichtsbehörde">
      <p className="text-black/70">
        Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu
        beschweren. Für das Land Bremen ist zuständig:
      </p>
      <div className="mt-3 rounded-2xl border border-black/10 p-4 bg-black/[0.02]">
        <div className="font-medium">
          Die Landesbeauftragte für Datenschutz und Informationsfreiheit der
          Freien Hansestadt Bremen
        </div>
        <div>Arndtstraße 1</div>
        <div>27570 Bremerhaven</div>
        <div className="mt-2">
          Telefon: 0421 361-2010
          <br />
          E-Mail:{" "}
          <a
            className="underline underline-offset-4"
            href="mailto:office@datenschutz.bremen.de"
          >
            office@datenschutz.bremen.de
          </a>
        </div>
        <div className="mt-2">
          <a
            href="https://www.datenschutz.bremen.de"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4"
          >
            www.datenschutz.bremen.de
          </a>
        </div>
      </div>
    </Section>

    <Section title="15. Änderungen dieser Datenschutzerklärung">
      <p className="text-black/70">
        Wir behalten uns vor, diese Datenschutzerklärung anzupassen, damit sie
        stets den aktuellen rechtlichen Anforderungen entspricht oder um
        Änderungen unserer Leistungen in der Datenschutzerklärung umzusetzen.
        Für Ihren erneuten Besuch gilt dann die neue Datenschutzerklärung.
      </p>
      <p className="text-black/50 text-xs mt-4">
        Stand: {new Date().toLocaleDateString("de-DE", { month: "long", year: "numeric" })}
      </p>
    </Section>
  </>
);
